/**
 * Stacking transforms.
 *
 * Two post-processing steps for stacked bar/line/area:
 * 1. **Show total** — adds an invisible "total" series with sum labels
 * 2. **Percent stacking** — normalizes values to 0–100% and adjusts Y axis
 */
import type { EChartsOption } from 'echarts';
import { formatChartValue } from '../../../../../utils/formatters';
import type { ChartStyleState, TypeStyle } from '../../../../edit/visualConfig';
import { isSingleObject } from './helpers';

// ── Show total labels ──────────────────────────────────────────────────────

/**
 * Add an invisible "total" series with sum labels on top of each stack.
 * Only applies when `showTotal` is true and stacking is enabled.
 */
export function applyShowTotal(
  option: EChartsOption,
  vc: ChartStyleState,
  cs: TypeStyle,
): EChartsOption {
  if (!vc.showTotal) return option;
  if (cs.stacking !== 'normal' && cs.stacking !== 'percent') return option;
  if (!Array.isArray(option.series)) return option;

  const allSeries = option.series as any[];
  const stackedSeries = allSeries.filter(
    (s) => (s.type === 'bar' || s.type === 'line') && Array.isArray(s.data),
  );
  if (stackedSeries.length === 0) return option;

  const result = { ...option };
  const len = stackedSeries[0].data.length;

  // Per-category totals
  const totals = new Array(len).fill(0);
  for (const s of stackedSeries) {
    for (let i = 0; i < len; i++) {
      totals[i] += Number(s.data[i]) || 0;
    }
  }

  // Invisible series that only renders the sum label.
  // For line-type series we need showSymbol: true (with size 0) because
  // ECharts only renders line labels at symbol anchor points.
  const isLine = stackedSeries[0].type === 'line';
  const totalSeries: any = {
    type: stackedSeries[0].type,
    name: '__total__',
    stack: 'total',
    data: totals.map(() => 0),
    itemStyle: { color: 'transparent', borderWidth: 0 },
    lineStyle: { width: 0, opacity: 0 },
    showSymbol: isLine,
    ...(isLine ? { symbolSize: 0 } : {}),
    emphasis: { disabled: true },
    label: {
      show: true,
      position: cs.horizontal ? 'right' : 'top',
      formatter: (params: any) => {
        const val = totals[params.dataIndex];
        if (vc.abbreviateNumbers) return formatChartValue(val);
        return val % 1 === 0 ? String(val) : parseFloat(val.toFixed(2)).toString();
      },
      fontSize: 11,
      fontWeight: 'bold' as const,
    },
    tooltip: { show: false },
  };
  result.series = [...allSeries, totalSeries];

  // Hide __total__ from legend
  if (isSingleObject(result.legend)) {
    const leg = result.legend as any;
    if (!leg.data) {
      leg.data = allSeries.map((s: any) => s.name).filter(Boolean);
    } else if (Array.isArray(leg.data)) {
      leg.data = (leg.data as string[]).filter((n: string) => n !== '__total__');
    }
  }

  return result;
}

// ── Percent stacking ───────────────────────────────────────────────────────

/**
 * Normalize stacked series values to percentages (0–100) and adjust the
 * Y axis accordingly. Only applies when `stacking === 'percent'`.
 */
export function applyPercentStacking(
  option: EChartsOption,
  cs: TypeStyle,
): EChartsOption {
  if (cs.stacking !== 'percent') return option;

  // For horizontal bars the value axis is X; for vertical bars it's Y.
  const valueAxisKey = cs.horizontal ? 'xAxis' : 'yAxis';
  if (!isSingleObject((option as any)[valueAxisKey])) return option;

  const result = { ...option };
  const va = (result as any)[valueAxisKey] as any;
  va.max = 100;
  va.min = 0;
  va.axisLabel = { ...va.axisLabel, formatter: (v: number) => `${v}%` };

  if (!Array.isArray(result.series)) return result;

  const allSeries = result.series as any[];
  const stackedSeries = allSeries.filter(
    (s) => (s.type === 'bar' || s.type === 'line') && Array.isArray(s.data),
  );
  if (stackedSeries.length <= 1) return result;

  const len = stackedSeries[0].data.length;

  // Per-category totals
  const totals = new Array(len).fill(0);
  for (const s of stackedSeries) {
    for (let i = 0; i < len; i++) {
      totals[i] += Math.abs(Number(s.data[i]) || 0);
    }
  }

  // Normalize each value to a percentage of its category total
  result.series = allSeries.map((s: any) => {
    if ((s.type !== 'bar' && s.type !== 'line') || !Array.isArray(s.data)) return s;
    return {
      ...s,
      data: s.data.map((v: any, i: number) => {
        const total = totals[i];
        return total > 0 ? +((Number(v) / total) * 100).toFixed(2) : 0;
      }),
    };
  });

  return result;
}